import React, { useEffect, useState, Fragment } from 'react'
import MobileNavigation from '../Navigation/MobileNavigation'
import { getBreadCrumbs } from '../../../../utils/utils'
import { getLinkType } from '../../../../utils/linkUtil'
import { Popover } from '../../../Compounds/src/Popover'

import { HeaderProps, RightContentEnum } from './Header.types'
import { BreadCrumbs, Menu, UtilityNavigation } from '../data/data.types'
import './App.scss'
import { LinkCompoundProps } from '../../../Compounds/src/LinkCompound'
import { ResetTool } from '../ResetTool/ResetTool'
import isEmpty from 'lodash/isEmpty'
import { APIENDPOINT, getCWPEnvironmentForURL } from '../../../../utils'

const env = getCWPEnvironmentForURL(window.location.href)

const PrintButton = (props: {
  togglePrintModalVisible: () => void
  label: string
  disable?: boolean
  ariaLabel?: string
}) => {
  return (
    <button
      data-analytics='RIG-save-PDF'
      onClick={props.togglePrintModalVisible}
      disabled={props.disable}
      data-tracking='Button'
      aria-label={props?.ariaLabel || props.label}
      className='Button btn icon u-border0 full u-textColorActionBlue u-textSizeMsrm1 u-textMedium'
    >
      <span
        className='adel-material-symbols-18 u-textColorActionBlue u-marginRight8'
        role='img'
        aria-hidden='true'
      >
        {props.label === 'Download page' ? 'draft' : 'file_copy'}
      </span>
      {props.label}
      {props.disable && (
        <div className='Loading Loading--small'>
          <div className='Loading-indicator'></div>
          <div className='Loading-indicator'></div>
          <div className='Loading-indicator'></div>
          <div className='Loading-indicator'></div>
        </div>
      )}
    </button>
  )
}

export const ResetButton = (props: {
  handleToolReset: () => void
  startOverAriaLabel?: string
  className?: string
}) => {
  return (
    <div>
      <button
        onClick={props.handleToolReset}
        className={`u-marginRight16 u-md-marginRight0 u-sm-marginRight0 Button Button--secondary Button--secondary--action-blue ${
          props.className ? props.className : ''
        }`}
        data-analytics='RIG-reset-tool'
        aria-label={props?.startOverAriaLabel || 'Start over'}
      >
        <span className='u-textSizeMsrm1'>Start over</span>
      </button>
    </div>
  )
}

const DEFAULT_MICROSITES_LINKS = [
  {
    text: 'Online security guarantee',
    href: '/privacy-security-fraud/online-security-guarantee',
    analytics: 'OnlineSecurityURL',
  } as LinkCompoundProps,
  {
    text: 'Customer service',
    href: '/customer-service',
    analytics: 'CustomerServiceUrl',
  } as LinkCompoundProps,
]

export const Header = ({
  headerDocument,
  mainMenu,
  backgroundColor,
  textColor,
  menuIconColor,
  layoutClass,
  dropdownOptions,
  mobileButton,
  rightContentType,
  resetModal,
  cleanLocalStorage,
  handDownloadPDF,
  rigProps,
  annualReportInfo,
  rpProps,
}: HeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleResetOpen = () => setIsModalOpen(true)
  const handleResetClose = () => setIsModalOpen(false)
  const handleResetSubmit = () => {
    setIsModalOpen(false)
    cleanLocalStorage && cleanLocalStorage()
  }
  const headerLogo =
    window?.AMPMSSITEPATH?.length > 2
      ? window.AMPMSSITEPATH + 'images/shared/Ameriprise_Financial_logo.jpg'
      : undefined
  const [popover, setPopover] = useState(false)

  const { desktopImage, siteName, siteNameHref, utilityNavigation } = headerDocument ?? {}
  let breadCrumbs: BreadCrumbs | undefined = undefined
  let menuItems: Menu[] = []
  if (mainMenu?.data?.menuItems) {
    const { data } = mainMenu
    menuItems = data.menuItems
    breadCrumbs = getBreadCrumbs(menuItems)
  }

  const handlePopoverClick = () => {
    const newPopoverValue: boolean = !popover
    const docBody = document.querySelector('body')
    if (docBody) {
      if (newPopoverValue) {
        docBody.style.overflow = 'hidden'
      } else {
        docBody.style.overflow = 'auto'
      }
    }
    setPopover(newPopoverValue)
  }
  useEffect(() => {
    const handleWindowSizeChange = () => {
      if (window.innerWidth >= 1025 && popover) {
        handlePopoverClick()
      }
    }
    window.addEventListener('resize', handleWindowSizeChange)
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange)
    }
  }, [popover])
  const buildNavItems = (navItems: UtilityNavigation[]) => {
    return (
      <>
        {navItems.map(item => {
          const customClasses = item.classes
            ? item.classes.reduce((att, curr) => {
                return `${att} ${curr}`
              }, '')
            : ''
          if (item.linkType === 'phone') {
            return (
              <a
                href={item.linkHref ? `tel:${item.linkHref}` : '#'}
                aria-label={item?.linkAltText || item.text}
                target={item.target}
                key={item.text}
                className={`${getLinkType(
                  item.linkType
                )} ${customClasses} u-linkClean u-md-hidden u-sm-hidden`}
                data-analytics={item.analytics}
              >
                {item.icon === 'phone' && (
                  <i>
                    <span
                      aria-hidden='true'
                      className='Icon u-textInheritColor u-textSizeMsrp1 u-alignMiddle u-marginRight4'
                    >
                      &#xe0cd;
                    </span>
                    <span className='u-hiddenVisually'>Phone number</span>
                  </i>
                )}
                {item.text}
              </a>
            )
          }
          return (
            <a
              href={item.linkHref}
              aria-label={item?.linkAltText || item.text}
              target={item.target}
              key={item.text}
              className={`${getLinkType(
                item.linkType
              )} ${customClasses} u-linkClean u-md-hidden u-sm-hidden`}
              data-analytics={item.analytics}
            >
              {item.icon === 'secure' && (
                <i className='Icon icon-shield u-marginRight4' aria-hidden='true'></i>
              )}
              {item.text}
            </a>
          )
        })}
      </>
    )
  }

  const getRightSideContent = () => {
    if (headerDocument && utilityNavigation?.length) {
      return buildNavItems(utilityNavigation)
    }
    if (dropdownOptions) {
      return (
        <Popover
          items={dropdownOptions.items}
          currentDisplayIndex={dropdownOptions.currentDisplayIndex}
          hiddenMessage={dropdownOptions.hiddenMessage}
        />
      )
    }

    if (rightContentType === RightContentEnum.impactAnalyzerAFG)
      return (
        <>
          <button
            className='Button Button--primary u-marginLeft12'
            onClick={handDownloadPDF}
            aria-label='Download Our Advantage PDF'
          >
            Download Our Advantage PDF
          </button>
          <button
            className='Button Button--secondary Button--secondary--action-blue'
            onClick={handleResetOpen}
            data-analytics={'Reset Tool'}
            aria-label={resetModal?.resetToolAriaLabel || 'Reset Tool'}
          >
            Reset Tool
          </button>
        </>
      )

    if (rightContentType === RightContentEnum.impactAnalyzerAAG)
      return (
        <button
          className='Button Button--secondary Button--secondary--action-blue'
          onClick={handleResetOpen}
          data-analytics={'Reset Tool'}
          aria-label={resetModal?.resetToolAriaLabel || 'Reset Tool'}
        >
          Reset Tool
        </button>
      )

    if (rightContentType === RightContentEnum.annualReport2025) {
      return (
        <div className='u-flex u-flexJustifyCenter u-flexAlignItemsCenter u-paddingLeft20 u-sm-paddingLeft0'>
          <a
            href={`mailto:?subject=${annualReportInfo?.mailSubject}&body=${annualReportInfo?.mailBody}`}
            aria-label={`Share ${annualReportInfo?.year} Annual Report via email`}
            className='adel-material-symbols-24 u-textColorActionBlue u-marginRight8'
          >
            mail
          </a>
          <a
            href={annualReportInfo?.pdfFilePath}
            aria-label={`Download ${annualReportInfo?.year} Annual Report as PDF`}
            className='adel-material-symbols-24 u-textColorActionBlue u-marginRight24'
            rel='external'
            target='_blank'
          >
            download{' '}
          </a>
          <div className='annualHeader-20YearImage u-flex'>
            <img
              src={APIENDPOINT(env).baseGallery + annualReportInfo?.yearsImage}
              alt={annualReportInfo?.yearsImageAltText}
            />
            {annualReportInfo?.yearsImageTitle && (
              <span className='annualHeader-20YearImageToolTip'>
                {annualReportInfo?.yearsImageTitle}
              </span>
            )}
          </div>
          {/* <img src={} /> */}
        </div>
      )
    }
    if (rightContentType === RightContentEnum.retirementIncome)
      return (
        !isEmpty(rigProps) && (
          <>
            {!rigProps.setupPage && !rigProps.welcomePage && (
              <div className='u-flex u-flexRow u-flexAlignItemsCenter u-textSizeMsrm1'>
                <PrintButton
                  togglePrintModalVisible={rigProps.printContent}
                  label={'Download page'}
                  disable={rigProps.singlePagePrinting}
                  ariaLabel={rigProps?.buttons?.downloadPageAriaLabel || ''}
                />
                <div className='u-borderLeft u-margin4 separator' />
                <PrintButton
                  togglePrintModalVisible={rigProps.printAllContent}
                  label={'Download all'}
                  disable={rigProps.allPagesPrinting}
                  ariaLabel={rigProps?.buttons?.downloadAllAriaLabel || ''}
                />
              </div>
            )}
            {!rigProps.setupPage && (
              <ResetButton
                handleToolReset={rigProps.handleToolReset}
                startOverAriaLabel={rigProps?.buttons?.startOverAriaLabel}
              />
            )}
          </>
        )
      )
    if (rightContentType === RightContentEnum.retirementProfile)
      return (
        !isEmpty(rpProps) && (
          <div className='u-flex u-flexJustifyCenter u-flexAlignItemsCenter u-paddingLeft20 u-sm-paddingLeft0'>
            <div className='PageHeader-buttonWrap u-sm-hidden u-md-flexRow u-lg-flexRow u-sm-marginTop20'>
              {rpProps.flowType === 'aac' ? (
                <button
                  data-analytics='RDE-share-your-results-btn'
                  aria-label='Request an appointment with an Ameriprise advisor'
                  onClick={() => rpProps.setShowModal('aac')}
                  className={
                    'Button Button--secondary Button--secondary--action-blue Button--small'
                  }
                >
                  {rpProps.buttons?.appointmentText}
                </button>
              ) : (
                <>
                  {!rpProps.hasAdvisor && rpProps.flowType !== 'crm' && (
                    <a
                      href='https://www.ameripriseadvisors.com/'
                      target='_blank'
                      rel='noopener noreferrer'
                      data-analytics='RDE-top-nav-find-an-advisor-btn'
                      className='Button Button--secondary Button--secondary--action-blue Button--small'
                    >
                      {rpProps.buttons?.findAdvisorText}
                    </a>
                  )}
                </>
              )}
              {rpProps.results && (
                <>
                  {rpProps.flowType === 'crm' && (
                    <>
                      {rpProps.shareResultsFailure && (
                        <div className='advisor__error'>
                          Something went wrong while sharing your results. Please try resharing or
                          try again later
                        </div>
                      )}
                      <button
                        data-analytics='RDE-share-your-results-btn'
                        aria-label='Download your results PDF'
                        onClick={rpProps.shareResultsWithAdvisor}
                        className={
                          'Button Button--secondary Button--secondary--action-blue Button--small'
                        }
                        disabled={rpProps.resultsShared}
                      >
                        {rpProps.resultsShared
                          ? rpProps.buttons?.sharedText
                          : rpProps.buttons?.shareText}
                      </button>
                    </>
                  )}
                  {rpProps.flowType !== 'crm' && rpProps.hasAdvisor && (
                    <button
                      className='Button Button--secondary Button--secondary--action-blue Button--small'
                      data-analytics='RDE-share-your-results-btn'
                      aria-label='Share your results with your advisor'
                      onClick={() =>
                        rpProps.setShowModal(
                          `${rpProps.flowType === 'referred' ? 'referred' : 'nonCrm'}`
                        )
                      }
                    >
                      {rpProps.buttons?.shareText}
                    </button>
                  )}

                  <div className='rdeamp-header_retake'>
                    <button
                      data-analytics='RDE-restart-quiz-with-arrow-btn'
                      className='btn'
                      aria-label='Restart the quiz'
                      onClick={rpProps.resetExperience}
                    >
                      <span className='adel-material-symbols-18' aria-hidden='true'>
                        replay
                      </span>
                      {rpProps.buttons?.restartText}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )
      )

    if (!utilityNavigation?.length) {
      return (
        <>
          <a
            href={DEFAULT_MICROSITES_LINKS[0].href}
            data-analytics={DEFAULT_MICROSITES_LINKS[0].analytics}
            className='Link u-linkClean u-flex u-flexAlignItemsCenter'
          >
            <span className='adel-material-symbols-16 u-marginRight4' aria-hidden='true' role='img'>
              security
            </span>
            {DEFAULT_MICROSITES_LINKS[0].text}
          </a>
          <div className='pipe--Divider u-posRelative' />
          <a
            href={DEFAULT_MICROSITES_LINKS[1].href}
            data-analytics={DEFAULT_MICROSITES_LINKS[1].analytics}
            className='Link u-linkClean'
          >
            {DEFAULT_MICROSITES_LINKS[1].text}
          </a>
        </>
      )
    }
    return null
  }
  const buildMobileGeneralNavItems = (): Array<LinkCompoundProps> => {
    let links: Array<LinkCompoundProps> = []
    if (siteName && siteNameHref) {
      links.push({
        text: siteName,
        href: siteNameHref,
      } as LinkCompoundProps)
    }
    if (utilityNavigation?.length) {
      links = [
        ...links,
        ...utilityNavigation.map(
          item =>
            ({
              text: item.text,
              href: item.linkHref,
              target: item.target,
              type: item.linkType,
              classes: item.classes,
            } as LinkCompoundProps)
        ),
      ]
    }
    if (dropdownOptions) {
      links = [
        ...links,
        ...dropdownOptions.items
          .filter((_, index) => {
            return index !== Number(dropdownOptions.currentDisplayIndex)
          })
          .map(
            item =>
              ({
                text: `${item.text} Careers`,
                href: item.href,
                target: item.target,
              } as LinkCompoundProps)
          ),
      ]
    }
    if (mobileButton) {
      links.push({
        text: mobileButton.text,
        href: mobileButton.href,
        analytics: mobileButton.analytics,
        type: 'default',
        target: '_blank',
      } as LinkCompoundProps)
    }
    if (links.length > 0) {
      return links
    }
    return DEFAULT_MICROSITES_LINKS
  }

  return (
    <>
      <header
        id='StandardHeader'
        className={`${
          layoutClass ? 'u-paddingRight16 u-sm-paddingLeft16 u-md-paddingLeft16' : ''
        } u-paddingVert16 ${backgroundColor} ${textColor}`}
      >
        <div className={layoutClass || 'u-sizeConstrained'}>
          <div className='amp-header-mobile-icon u-flex u-flexRow u-flexJustifyBetween u-flexAlignItemsCenter'>
            <button
              className='u-lg-hidden amp-hamburger u-flex u-flexJustifyCenter u-flexAlignItemsCenter u-overflowHidden'
              onClick={handlePopoverClick}
              data-testid={'popover-button'}
              aria-expanded={popover}
            >
              {popover ? (
                <i>
                  <span aria-hidden='true' className={`Icon u-textSizeMsrp4 ${textColor}`}>
                    &#xe5cd;
                  </span>
                  <span className={`u-hiddenVisually ${textColor}`}>Close Menu</span>
                </i>
              ) : (
                <div className='u-flex u-flexCol u-flexJustifyCenter u-flexAlignItemsCenter u-sizeFull'>
                  <div className='amp-hamburger-icon'>
                    <div aria-hidden='true' className={`${textColor}`}>
                      <div
                        className={`amp-hamburger-icon-dash u-marginTop4 u-marginBottom4 ${menuIconColor}`}
                      >
                        &nbsp;
                      </div>
                      <div
                        className={`amp-hamburger-icon-dash u-marginTop4 u-marginBottom4 ${menuIconColor}`}
                      >
                        &nbsp;
                      </div>
                      <div
                        className={`amp-hamburger-icon-dash u-marginTop4 u-marginBottom4 ${menuIconColor}`}
                      >
                        &nbsp;
                      </div>
                    </div>
                    <span className={`u-hiddenVisually ${textColor}`}>MENU</span>
                  </div>
                  <div
                    className={`amp-hamburger-label u-textSizeMsrm2 ${textColor}`}
                    data-qa='HamburgerMenu'
                  >
                    MENU
                  </div>
                </div>
              )}
            </button>
            <a href='/' className='u-linkClean'>
              <img className='' alt='ameriprise logo' src={desktopImage ?? headerLogo} />
            </a>
            {rightContentType === RightContentEnum.annualReport2025 && (
              <div className='u-flex u-flexRow u-md-flexCol u-sm-flexCol u-flexAlignItemsCenter'>
                <p
                  className='annualreport-HeaderName u-textBold
                   u-lg-textSizeMsrp3 u-md-textSizeMsrm2 u-sm-textSizeMsrm2 u-marginRight4 u-textColorMidnightBlue'
                >
                  2025
                </p>
                <p className='annualreport-HeaderName u-lg-textSizeMsrp3 u-md-textSizeMsrm2 u-sm-textSizeMsrm2 u-textColorMidnightBlue'>
                  Annual Report
                </p>
                <div className='u-lg-hidden'>
                  <a
                    href={`mailto:?subject=${annualReportInfo?.mailSubject}&body=${annualReportInfo?.mailBody}`}
                    aria-label={`Share ${annualReportInfo?.year} Annual Report via email`}
                    className='adel-material-symbols-24 u-textColorActionBlue u-marginRight8'
                  >
                    mail
                  </a>
                  <a
                    href={annualReportInfo?.pdfFilePath}
                    aria-label={`Download ${annualReportInfo?.year} Annual Report as PDF`}
                    className='adel-material-symbols-24 u-textColorActionBlue u-marginRight24'
                    rel='external'
                    target='_blank'
                  >
                    download{' '}
                  </a>
                </div>
              </div>
            )}
            <div className='header--Buttons u-flex u-flexRow u-flexJustifyBetween u-flexAlignItemsCenter u-textSizeMsrm1 u-sm-hidden u-md-hidden'>
              {siteName && siteNameHref && (
                <div className={`u-marginHoriz16 ${textColor}`}>
                  <a
                    className={`u-linkClean ${textColor}`}
                    href={siteNameHref}
                    data-analytics={siteName}
                  >
                    {siteName}
                  </a>
                </div>
              )}
              {getRightSideContent()}
            </div>
          </div>
        </div>
      </header>
      {(rightContentType === RightContentEnum.impactAnalyzerAAG ||
        rightContentType === RightContentEnum.impactAnalyzerAFG) &&
      !isEmpty(resetModal) ? (
        <ResetTool
          reset={resetModal}
          isOpen={isModalOpen}
          onClose={handleResetClose}
          onSubmit={handleResetSubmit}
        />
      ) : null}
      {breadCrumbs &&
        breadCrumbs?.currentPageParams?.breadcrumbDisable !== 'true' &&
        breadCrumbs?.breadCrumbs.length !== 0 && (
          <nav className='breadcrumbs u-lg-hidden u-md-paddingHoriz8 u-sm-paddingLeft16'>
            <ul>
              {breadCrumbs.breadCrumbs.map(breadCrumb => (
                <Fragment key={breadCrumb.pageName}>
                  <span
                    aria-hidden='true'
                    className='Icon u-textSizeMsrm1 List-item u-paddingVert8 u-paddingHoriz8'
                  >
                    &#xe314;
                  </span>
                  <li className='u-textSizeMsrm1 List-item u-paddingVert8 u-paddingHoriz0'>
                    <a
                      data-testid={breadCrumb.pageName}
                      href={breadCrumb.pageURL}
                      className={`u-textSizeMsrm1 ${
                        breadCrumbs?.currentPageParams.breadcrumbReverse === 'true' &&
                        'u-textColorWhite'
                      }`}
                    >
                      {breadCrumb.pageName}
                    </a>
                  </li>
                </Fragment>
              ))}
            </ul>
          </nav>
        )}
      {popover && (
        <MobileNavigation
          menuItems={menuItems}
          links={buildMobileGeneralNavItems()}
          onClick={handlePopoverClick}
          resetModal={resetModal}
          isOpen={isModalOpen}
          onClose={handleResetClose}
          onSubmit={handleResetSubmit}
          rightContentType={rightContentType}
          handleOpen={handleResetOpen}
          rigProps={rigProps}
          handDownloadPDF={handDownloadPDF}
        />
      )}
    </>
  )
}

export default Header
